import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Assessment as AssessmentIcon,
  Message as MessageIcon,
  DataObject as DataObjectIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

const Dashboard: React.FC = () => {
  const { user, session } = useAuth();
  const [stats, setStats] = useState({
    projects: 0,
    reports: 0,
    messages: 0,
    submissions: 0,
  });
  const [recentProjects, setRecentProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Attendre que la session soit disponible avant de charger
    if (session) {
      fetchDashboardData();
    }
  }, [session]);

  const fetchDashboardData = async () => {
    try {
      const [projectsRes, reportsRes, messagesRes, submissionsRes] = await Promise.all([
        api.projects.getAll().catch(() => ({ projects: [] })),
        api.reports.getAll().catch(() => ({ reports: [] })),
        api.messages.getAll().catch(() => ({ messages: [] })),
        api.forms.getSubmissions().catch(() => ({ submissions: [] })),
      ]);

      setStats({
        projects: projectsRes.projects?.length || 0,
        reports: reportsRes.reports?.length || 0,
        messages: messagesRes.messages?.length || 0,
        submissions: submissionsRes.submissions?.length || 0,
      });

      setRecentProjects(projectsRes.projects?.slice(0, 5) || []);
    } catch (error) {
      console.error('Erreur lors du chargement du tableau de bord:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{ title: string; value: number; icon: React.ReactNode; color: string }> = ({
    title,
    value,
    icon,
    color,
  }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4">{value}</Typography>
          </Box>
          <Box sx={{ color, fontSize: 40 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Tableau de bord
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Projets"
            value={stats.projects}
            icon={<FolderIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rapports Terrain"
            value={stats.reports}
            icon={<AssessmentIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Messages"
            value={stats.messages}
            icon={<MessageIcon />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Soumissions"
            value={stats.submissions}
            icon={<DataObjectIcon />}
            color="#9c27b0"
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Projets récents
            </Typography>
            <List>
              {recentProjects.length === 0 ? (
                <Typography color="textSecondary">Aucun projet pour le moment</Typography>
              ) : (
                recentProjects.map((project) => (
                  <ListItem key={project.id}>
                    <ListItemText
                      primary={project.title}
                      secondary={project.description || 'Aucune description'}
                    />
                    <Chip
                      label={
                        project.status === 'completed' ? 'Terminé' :
                        project.status === 'in_progress' ? 'En Cours' :
                        project.status === 'pending' ? 'En Attente' :
                        project.status === 'review' ? 'En Revue SOBEBRA' :
                        project.status === 'cancelled' ? 'Annulé' :
                        project.status === 'archived' ? 'Archivé' : project.status
                      }
                      size="small"
                      color={
                        project.status === 'completed'
                          ? 'success'
                          : project.status === 'in_progress'
                          ? 'primary'
                          : 'default'
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Actions rapides
            </Typography>
            <List>
              <ListItem button>
                <ListItemText primary="Créer un nouveau projet" />
              </ListItem>
              <ListItem button>
                <ListItemText primary="Envoyer un message" />
              </ListItem>
              <ListItem button>
                <ListItemText primary="Soumettre un rapport" />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;

